import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, isSpendApprovalError } from './api';
import { streamAudrey } from './audreyStream';
import { Send, Plus, Sparkles, Trash2, Bot, User as UserIcon, MessageSquare, BookOpen, History, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import SimpleMarkdown from '../components/SimpleMarkdown';
import { formatDate } from './kit/time';
import { Input } from '../components/ui/input';

export const starters = [
  { title: 'FHA vs Conventional', prompt: 'Compare FHA and Conventional loans for a first-time buyer with a 680 credit score and 5% down.' },
  { title: 'Self-employed income', prompt: 'What do the guidelines say about qualifying income for a self-employed borrower with 2023 & 2024 Schedule C returns? Quote the rules and point me to the right calculator for the numbers.' },
  { title: 'DTI ratio breakdown', prompt: 'Walk me through front-end and back-end DTI: what counts and what doesn\'t, and the limits the guidelines set. Point me to the DTI calculator for the math.' },
  { title: 'Draft a follow-up email', prompt: 'Draft a warm, compliant follow-up email to a borrower who went silent 2 weeks after pre-approval.', live: true },
];

// ============================================================================
// Guideline Deep Dive — curated prompts that showcase Audrey's KB
// ============================================================================
const AGENCY_STYLE = {
  fannie:  { bg: '#ECFDF5', color: '#065F46', label: 'FNMA' },
  freddie: { bg: '#EFF6FF', color: '#1E40AF', label: 'FHLMC' },
  fha:     { bg: '#FFF7ED', color: '#9A3412', label: 'FHA' },
  va:      { bg: '#FEF3C7', color: '#7C2D12', label: 'VA' },
  usda:    { bg: '#F0FDF4', color: '#166534', label: 'USDA' },
  compare: { bg: '#F1F5F9', color: '#334155', label: 'COMPARE' },
};

export const DEEP_DIVE_GROUPS = [
  {
    title: 'Self-Employed',
    prompts: [
      { agency: 'freddie', title: 'Every Freddie 1120S add-back', prompt: 'Show me every add-back Freddie Form 91 allows on Form 1120S — line by line with the Sch M-1 references. What differs vs Fannie 1084?' },
      { agency: 'fannie',  title: 'Fannie 1084 Line 3e explained', prompt: 'Why does Fannie 1084 SUBTRACT the Meals 50% exclusion (Line 3e) and does Freddie Form 91 do the same? Cite the exact sections.' },
      { agency: 'fha',     title: 'FHA 2025 business-use-of-home', prompt: 'What changed in the 2025 FHA HB 4000.1 update regarding Business Use of Home add-back for Schedule C? Cite the section.' },
      { agency: 'compare', title: 'Declining income across 5 agencies', prompt: 'A self-employed borrower\'s net profit dropped from 2023 to 2024. How would Fannie, Freddie, FHA, VA, and USDA each treat this declining income? Quote each rule and point me to the income calculator for the numbers.' },
    ],
  },
  {
    title: 'Rental Income',
    prompts: [
      { agency: 'fha',     title: 'Negative rental → liability', prompt: 'For an FHA loan, how do I handle negative net rental income on a 2-4 unit primary? Cite the exact section.' },
      { agency: 'compare', title: 'Rental income treatment: 5 agencies', prompt: 'Compare rental income treatment across Fannie, Freddie, FHA, VA, and USDA. Which agencies allow 75% of gross rents, which require the Schedule E method, and how do they handle negative rental?' },
      { agency: 'freddie', title: 'Freddie Form 92 mechanics', prompt: 'Walk me through Freddie Form 92 net rental income calculation step by step. What add-backs apply, and how does the "months in service" divisor work?' },
      { agency: 'va',      title: 'VA 12-mo lease requirement', prompt: 'Does VA require a 12-month lease OR a 24-month rental history for existing rentals? What if the borrower has neither?' },
    ],
  },
  {
    title: 'DTI, Compensating Factors & Residual',
    prompts: [
      { agency: 'compare', title: 'DTI ceilings across 5 agencies', prompt: 'Compare the maximum DTI thresholds and compensating factors required for Fannie, Freddie, FHA, VA, and USDA — including automated underwriting (DU/LPA/GUS/TOTAL Scorecard) differences.' },
      { agency: 'va',      title: 'VA residual income table', prompt: 'Explain VA residual income: what it is, what the guideline says about the regional table, and when exceeding it helps me qualify above 41% DTI?' },
      { agency: 'usda',    title: 'USDA GUS 29/41 vs 32/44', prompt: 'When can I use the higher USDA GUS DTI thresholds of 32/44? What compensating factors are required?' },
      { agency: 'fha',     title: 'FHA compensating factors', prompt: 'List all FHA compensating factors that support DTI above baseline, with the reserves and payment-shock requirements for each.' },
    ],
  },
  {
    title: 'Non-Taxable Income & Gross-Up',
    prompts: [
      { agency: 'compare', title: 'Gross-up rates by agency', prompt: 'What is the non-taxable income gross-up rate for each agency? Include SS, VA disability, child support, and non-taxable pension.' },
      { agency: 'fha',     title: 'FHA 15% vs Fannie 25%', prompt: 'Why does FHA gross up non-taxable income at only 15% while Fannie/Freddie allow 25%? What documentation can override the default?' },
      { agency: 'va',      title: 'VA disability + BAH treatment', prompt: 'How do I count VA disability, DIC, BAH, and BAS on a VA loan? Are they grossed up? Continuation requirements?' },
    ],
  },
  {
    title: 'USDA-Specific',
    prompts: [
      { agency: 'usda',    title: 'USDA adjusted annual income', prompt: 'Explain USDA adjusted annual income vs. repayment income — including all household member inclusions and the $480 dependent / elderly / disability / medical deductions.' },
      { agency: 'usda',    title: '115% AMI eligibility', prompt: 'How do I check whether a borrower is within the 115% AMI income limit for USDA in a specific county? What happens if they\'re slightly over?' },
    ],
  },
  {
    title: 'Variable Income (OT/Bonus/Commission)',
    prompts: [
      { agency: 'compare', title: '24-mo averaging rules by agency', prompt: 'For overtime, bonus, and commission income — what averaging methodology does each of the 5 agencies use? When can I use less than 24 months?' },
      { agency: 'fannie',  title: 'Commission ≥ 25% rule', prompt: 'When commission is 25% or more of total employment income under Fannie Mae, what additional documentation is required and how are unreimbursed business expenses treated?' },
    ],
  },
];

export default function AudreyProPage() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Below md the sessions list is an overlay opened from the chat header.
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef(null);
  // Bumped whenever the user opens another conversation, so a reply still streaming in
  // does not draw itself into the one now on screen.
  const viewRef = useRef(0);

  const loadSessions = async () => {
    const { data } = await api.get('/chat/sessions');
    setSessions(data);
    return data;
  };

  const loadMessages = async (id) => {
    viewRef.current += 1;
    setActive(id);
    const { data } = await api.get(`/chat/sessions/${id}/messages`);
    setMessages(data);
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const newChat = () => { viewRef.current += 1; setActive(null); setMessages([]); };

  // The page height assumed a fixed-height top nav, but the impersonation and
  // owner-mode banners can stack above it, pushing the composer below the
  // viewport. Measure the actual sticky header's bottom edge instead of
  // hardcoding its height.
  const [chromeHeight, setChromeHeight] = useState(64);
  useLayoutEffect(() => {
    const measure = () => {
      const header = document.querySelector('[data-testid="global-topnav"]');
      if (header) setChromeHeight(header.getBoundingClientRect().bottom);
    };
    measure();
    window.addEventListener('resize', measure);
    document.addEventListener('click', measure);
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('click', measure);
    };
  }, []);

  const send = async (textOverride, { forceLive = false } = {}) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput(''); setSending(true);
    const stamp = Date.now();
    const userMsg = { role: 'user', content: text, message_id: 'tmp_' + stamp };
    const draftId = 'draft_' + stamp;
    const view = viewRef.current;
    setMessages((m) => [...m, userMsg]);
    let draft = '';
    let final = null;
    let failure = null;
    const onEvent = ({ event, data }) => {
      if (event === 'delta') {
        if (viewRef.current !== view) return;
        draft += data.text;
        const content = draft;
        setMessages((m) => (m.some((x) => x.message_id === draftId)
          ? m.map((x) => (x.message_id === draftId ? { ...x, content } : x))
          : [...m, { role: 'assistant', content, message_id: draftId }]));
      } else if (event === 'done') final = data;
      else if (event === 'error') failure = data;
    };
    try {
      await streamAudrey({ session_id: active, message: text, force_live: forceLive }, onEvent);
      if (failure || !final) throw { response: { status: failure?.status, data: { detail: failure?.detail } } };
      // From here the server has saved the exchange: nothing below may undo the question.
      loadSessions().catch(() => {});
      if (viewRef.current !== view) return;
      setActive(final.session_id);
      // The saved reply is the answer: the grounding check can replace what streamed.
      let msgs = null;
      try {
        msgs = (await api.get(`/chat/sessions/${final.session_id}/messages`)).data;
      } catch { /* fall back to what the send returned */ }
      if (viewRef.current !== view) return;
      if (!msgs) {
        setMessages((m) => [...m.filter((x) => x.message_id !== userMsg.message_id && x.message_id !== draftId), userMsg, final.message]);
        return;
      }
      if (msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        if (last.role === 'assistant' && final.citations && final.citations.length > 0) {
          last.citations = final.citations;
        }
      }
      setMessages(msgs);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const retry = e.response?.headers?.['retry-after'];
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({
        variant: e.response?.status === 429 ? 'blocked' : 'destructive',
        title: e.response?.status === 429 ? 'Audrey needs a breather' : 'AI error',
        description: typeof detail === 'string' ? detail : (retry ? `Try again in about ${Math.ceil(Number(retry) / 60)} min` : 'Try again'),
      });
      // The server took the question back out; so does the screen, and the text goes back in the box.
      setMessages((m) => m.filter(msg => msg.message_id !== userMsg.message_id && msg.message_id !== draftId));
      if (viewRef.current === view) setInput((cur) => cur || text);
    } finally { setSending(false); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!(await confirmDialog({ title: 'Delete this conversation?', confirmText: 'Delete', destructive: true }))) return;
    try {
      await api.delete(`/chat/sessions/${id}`);
      if (active === id) newChat();
      loadSessions();
    } catch { toast({ title: 'Delete failed' }); }
  };

  return (
    <div className="flex" style={{ height: `calc(100vh - ${chromeHeight}px)` }}>
      {/* Sessions list */}
      <aside className={`${showHistory ? 'flex fixed inset-0 z-50' : 'hidden'} md:static md:z-auto md:flex w-full md:w-72 border-r border-slate-200 bg-white flex-col`} data-testid="audrey-sessions">
        <div className="p-4 flex items-center gap-2">
          <button onClick={() => { newChat(); setShowHistory(false); }} className="btn-gold flex-1 justify-center"><Plus size={15} /> New Chat</button>
          <button onClick={() => setShowHistory(false)} className="md:hidden p-2 text-slate-500" aria-label="Close history"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 px-3 mb-2">Recent</div>
          {sessions.length === 0 && <div className="text-slate-400 text-sm px-3">No conversations yet.</div>}
          {sessions.map((s) => (
            <div
              key={s.session_id}
              role="button"
              tabIndex={0}
              onClick={() => { loadMessages(s.session_id); setShowHistory(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { loadMessages(s.session_id); setShowHistory(false); } }}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 group cursor-pointer ${active === s.session_id ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <MessageSquare size={13} className="text-slate-400 flex-shrink-0" />
                  <span className="truncate font-medium" style={{ color: '#070B5F' }}>{s.title}</span>
                </div>
                <button onClick={(e) => del(s.session_id, e)} className="md:opacity-0 md:group-hover:opacity-100 text-slate-400 md:text-inherit hover:text-red-500 flex-shrink-0"><Trash2 size={12} /></button>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{formatDate(s.updated_at)}</div>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat pane */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="px-3 py-3 md:px-6 md:py-4 border-b border-slate-200 bg-white flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button onClick={() => setShowHistory(true)} className="md:hidden p-2 -ml-1 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Conversations" data-testid="audrey-history-toggle"><History size={18} /></button>
            <div className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #A6E56C, #6BA82E)' }}>
              <Sparkles size={18} style={{ color: '#070B5F' }} />
            </div>
            <div className="min-w-0">
              <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>Audrey Pro</div>
              <div className="text-xs text-slate-500 truncate">Your AI mortgage co-pilot</div>
            </div>
          </div>
          <Link to="/portal/ai/guidelines" className="inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid="browse-guidelines-link">
            <BookOpen size={15} /> <span className="hidden sm:inline">Browse guidelines</span><span className="sm:hidden">Guidelines</span>
          </Link>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 md:p-6 space-y-4" style={{ background: '#F5F6FA' }}>
          {messages.length === 0 && !sending && (
            <div className="max-w-2xl mx-auto text-center py-12">
              <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #A6E56C, #6BA82E)' }}>
                <Sparkles size={28} style={{ color: '#070B5F' }} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold mt-4" style={{ color: '#070B5F' }}>How can I help you close more loans?</h2>
              <p className="text-slate-500 mt-2">Ask about guidelines, income calc, LOEs, or draft borrower comms.</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-8 text-left">
                {starters.map((s) => (
                  <button key={s.title} onClick={() => send(s.prompt)} className="p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#6BA82E] hover:shadow-md transition text-left">
                    <div className="font-semibold text-sm mb-1" style={{ color: '#070B5F' }}>{s.title}</div>
                    <div className="text-xs text-slate-500">{s.prompt.slice(0, 90)}...</div>
                  </button>
                ))}
              </div>

              {/* Guideline Deep Dive — powered by the knowledge base */}
              <div className="mt-12 text-left" data-testid="guideline-deep-dive">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #A6E56C, #6BA82E)' }}>
                    <BookOpen size={18} style={{ color: '#070B5F' }} />
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Powered by the Knowledge Base</div>
                    <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Guideline Deep Dive</div>
                  </div>
                </div>
                <div className="space-y-6">
                  {DEEP_DIVE_GROUPS.map((group) => (
                    <div key={group.title}>
                      <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 pl-1">
                        {group.title}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {group.prompts.map((p) => {
                          const s = AGENCY_STYLE[p.agency] || AGENCY_STYLE.compare;
                          return (
                            <button
                              key={p.title}
                              onClick={() => send(p.prompt)}
                              data-testid={`deep-dive-${p.agency}-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
                              className="p-3 rounded-xl bg-white border border-slate-200 hover:border-[#6BA82E] hover:shadow-sm transition text-left group"
                            >
                              <div className="flex items-start gap-2">
                                <span
                                  className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5"
                                  style={{ background: s.bg, color: s.color }}
                                >
                                  {s.label}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="font-semibold text-xs mb-0.5" style={{ color: '#070B5F' }}>{p.title}</div>
                                  <div className="text-[11px] text-slate-500 leading-snug line-clamp-2">{p.prompt}</div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.message_id} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0`} style={m.role === 'user' ? { background: '#070B5F', color: '#A6E56C' } : { background: 'linear-gradient(135deg, #A6E56C, #6BA82E)', color: '#070B5F' }}>
                {m.role === 'user' ? <UserIcon size={16} /> : <Bot size={16} />}
              </div>
              <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === 'user' ? 'text-white whitespace-pre-wrap' : 'bg-white border border-slate-100'}`} style={m.role === 'user' ? { background: '#070B5F', borderBottomRightRadius: 4 } : { color: '#0f172a', borderBottomLeftRadius: 4 }}>
                {m.role === 'assistant' ? <SimpleMarkdown text={m.content} /> : m.content}
                {m.role === 'assistant' && m.canned_generated_at && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-testid="prepared-answer-note">
                    Prepared answer from {formatDate(m.canned_generated_at)}, written from the guidelines.{' '}
                    {messages[i - 1]?.role === 'user' && (
                      <button
                        type="button"
                        disabled={sending}
                        onClick={() => send(messages[i - 1].content, { forceLive: true })}
                        className="font-semibold underline disabled:opacity-50"
                        style={{ color: '#070B5F' }}
                      >
                        Ask Audrey live
                      </button>
                    )}
                  </div>
                )}
                {m.role === 'assistant' && m.citations && m.citations.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#6BA82E' }}>Sources</div>
                    <div className="flex flex-wrap gap-1.5">
                      {m.citations.map((c) => (
                        <a
                          key={c.id}
                          href={c.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`citation-${c.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border transition hover:shadow-sm"
                          style={{
                            background: '#F8FAFC',
                            borderColor: '#e2e8f0',
                            color: '#070B5F',
                          }}
                          title={c.section_title}
                        >
                          <span className="opacity-70">{c.agency.toUpperCase()}</span>
                          <span>§{c.section_id}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && !messages.some((m) => String(m.message_id).startsWith('draft_')) && (
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #A6E56C, #6BA82E)' }}>
                <Bot size={16} style={{ color: '#070B5F' }} />
              </div>
              <div className="bg-white border border-slate-100 rounded-2xl px-4 py-3 flex gap-1.5" style={{ borderBottomLeftRadius: 4 }}>
                {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-slate-300" style={{ animation: 'audreyPulse 1s ease-in-out infinite', animationDelay: `${i*150}ms` }} />)}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 md:p-4 bg-white border-t border-slate-200">
          <div className="max-w-4xl mx-auto flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Audrey anything about mortgages..."
              className="flex-1 min-w-0 w-auto h-auto px-4 py-3 rounded-full md:text-base" />
            <button type="submit" disabled={sending || !input.trim()} className="btn-gold px-5"><Send size={16} /></button>
          </div>
        </form>
      </div>
    </div>
  );
}
